// (C) Copyright IBM Deutschland GmbH 2021. All rights reserved.

import React from 'react';
import {View, StyleSheet, Text, Image, Linking, ScrollView} from 'react-native';
import {Button, IconButton} from 'react-native-paper';
import {Picker} from '@react-native-picker/picker';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {theme} from '../config';
import appName from '../appName.json';
import VersionCheck from '../components/shared/VersionCheck';
import translate, {availableLanguages, getLanguageTag} from '../services/localization';
import {updateLanguage} from '../store/globals.slice';
import {Routes} from '../navigation/constants';

function MaintenanceModeScreen({infoText, infoURL, navigation}) {
  const dispatch = useDispatch();
  // Subscribe so translated labels update when the language changes.
  const {fontScaleFactor = 1, language_code: languageTag} = useSelector(state => state.Globals);
  const copy = translate('maintenance');
  const imprint = translate('webViews')[0];
  const version = `${VersionCheck.getCurrentVersion()}, Build ${VersionCheck.getCurrentBuildNumber()}`;
  const scaled = size => ({fontSize: size * fontScaleFactor, lineHeight: size * fontScaleFactor * 1.6});

  return (
    <ScrollView contentContainerStyle={styles.page}>
      <View style={styles.wrapper}>
        <View style={styles.brand}>
          <Image
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel={appName.displayName}
            source={require('../CUSTOMIZATION/images/mii_acribis_landscape.png')}
          />
          <Text accessibilityRole="header" style={[styles.appName, scaled(24)]}>
            {appName.displayName}
          </Text>
        </View>

        <View style={styles.card}>
          <View style={styles.status}>
            <IconButton icon="wrench-outline" iconColor={theme.colors.success} size={28} accessible={false} />
          </View>
          <Text accessibilityRole="header" style={[styles.title, scaled(30)]}>{copy.title}</Text>
          <Text style={[styles.lead, scaled(17)]}>{infoText || copy.description}</Text>
          <Text style={[styles.body, scaled(15)]}>{copy.body}</Text>
          <View style={styles.notice}>
            <Text style={[styles.noticeText, scaled(14)]}>{copy.recovery}</Text>
          </View>
          {/^https?:\/\//i.test(infoURL || '') && (
            <Button mode="contained" buttonColor={theme.colors.success} onPress={() => Linking.openURL(infoURL)}
              style={styles.infoButton}>{copy.moreInformation}</Button>
          )}
          <Text style={[styles.thanks, scaled(14)]}>{copy.thanks}</Text>
        </View>

        <View style={styles.footer}>
          <Text style={[styles.languageLabel, scaled(13)]}>{translate('about').languageSelection}</Text>
          <Picker accessibilityLabel={translate('about').languageSelection} style={styles.languagePicker}
            selectedValue={languageTag || getLanguageTag()}
            onValueChange={value => {
              if (value !== getLanguageTag()) dispatch(updateLanguage({languageTag: value}));
            }}>
            {Object.keys(availableLanguages).map(key => (
              <Picker.Item key={key} label={availableLanguages[key].title} value={key} />
            ))}
          </Picker>
          <Text style={[styles.credits, scaled(13)]}>
            {translate('about').devNotice}.{'\n'}
            © {new Date().getFullYear()}. {translate('about').allRightsReserved}.
          </Text>
          <View style={styles.links}>
            <Button textColor={theme.colors.success} onPress={() => Linking.openURL(imprint.uri)}>{imprint.title}</Button>
            <Button textColor={theme.colors.success} onPress={() => navigation.navigate(Routes.PRIVACY_POLICY)}>
              {translate('about').legal.title}
            </Button>
            <Button textColor={theme.colors.success} onPress={() => navigation.navigate(Routes.TERMS_CONDITIONS)}>
              {translate('legalInformation').title}
            </Button>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

MaintenanceModeScreen.propTypes = {
  infoText: PropTypes.string,
  infoURL: PropTypes.string,
  navigation: PropTypes.shape({navigate: PropTypes.func.isRequired}).isRequired,
};

const styles = StyleSheet.create({
  page: {flexGrow: 1, backgroundColor: theme.colors.accent3, paddingHorizontal: 20, paddingVertical: 32},
  wrapper: {width: '100%', maxWidth: 760, alignSelf: 'center', flexGrow: 1},
  brand: {alignItems: 'center', marginBottom: 28},
  logo: {width: 240, maxWidth: '100%', height: 76, marginBottom: 16},
  appName: {fontWeight: '700', color: theme.colors.success, textAlign: 'center'},
  card: {backgroundColor: theme.colors.white, borderRadius: 20, paddingHorizontal: 24, paddingVertical: 32,
    alignItems: 'center', borderWidth: 1, borderColor: theme.colors.accent2, borderTopWidth: 5,
    borderTopColor: theme.colors.primary},
  status: {backgroundColor: theme.colors.primary_very_light, borderRadius: 40, marginBottom: 16},
  title: {fontWeight: '700', color: theme.colors.accent4, textAlign: 'center', marginBottom: 20},
  lead: {color: theme.colors.accent4, textAlign: 'center', lineHeight: 28, maxWidth: 580},
  body: {color: theme.colors.copyrightNotice, textAlign: 'center', lineHeight: 25, marginTop: 16, maxWidth: 560},
  notice: {backgroundColor: theme.colors.primary_very_light, borderRadius: 10, padding: 16, marginTop: 24, width: '100%'},
  noticeText: {color: theme.colors.accent4, textAlign: 'center', lineHeight: 23},
  infoButton: {marginTop: 24},
  thanks: {color: theme.colors.copyrightNotice, textAlign: 'center', marginTop: 24},
  footer: {alignItems: 'center', paddingTop: 28},
  languageLabel: {color: theme.colors.copyrightNotice, marginBottom: 8},
  languagePicker: {width: 180, maxWidth: '100%', padding: 8, color: theme.colors.accent4},
  credits: {color: theme.colors.copyrightNotice, textAlign: 'center', lineHeight: 21, marginTop: 20},
  links: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', marginTop: 12},
  version: {color: theme.colors.copyrightNotice, marginTop: 12, textAlign: 'center'},
});

export default MaintenanceModeScreen;
